import React, { useMemo, useState } from "react";
import {
  IconArrowDown,
  IconArrowUp,
  IconCheck,
  IconPlus,
  IconRotate,
  IconX,
} from "@tabler/icons-react";
import {
  useEncoderCatalog,
  useEncoderCodeValidate,
  useEncoderCptEdits,
  useEncoderInstructions,
} from "../../hooks/queries";
import type {
  CodeSearchResult,
  CptEditsResult,
  EncoderIcdValidationResult,
  EncoderScratchCode,
} from "../../types/encoder";
import {
  alertsFromIcdInstructions,
  cn,
  encoderErrorMessage,
  findAggregatedIcdCode,
  isEncoderDxType,
  isEncoderIcdResultType,
  isEncoderCptResultType,
  newScratchId,
  normalizeEncoderCode,
  todayDdMmYyyy,
} from "../../utils/encoder";
import { useEncoderUi } from "./context";
import {
  CodeLabels,
  IcdInstructionCards,
  MergedCodeSearch,
  Spinner,
  ValidationResultCard,
} from "./shared";

type EditorCode = EncoderScratchCode;

const sameEncoderCode = (left: string, right: string): boolean =>
  Boolean(left) &&
  Boolean(right) &&
  normalizeEncoderCode(left) === normalizeEncoderCode(right);

const validationSeverity = (
  row: EncoderIcdValidationResult
): "error" | "warning" | "info" | null => {
  const severity = row.severity.trim().toLowerCase();
  if (severity === "error") return "error";
  if (severity === "info") return "info";
  if (severity === "ok" || severity === "pass" || severity === "none") return null;
  if (severity === "warning") return "warning";
  if (row.instruction.trim() || row.message.trim() || row.note.trim()) {
    return "warning";
  }
  return null;
};

const resultsForCode = (
  rows: EncoderIcdValidationResult[],
  code: string,
  allDxCodes: string[]
): EncoderIcdValidationResult[] => {
  const direct = rows.filter(
    (row) =>
      sameEncoderCode(row.code_1, code) || sameEncoderCode(row.code_2, code)
  );
  if (direct.length) return direct;
  const unassigned = rows.filter(
    (row) => !row.code_1.trim() && !row.code_2.trim()
  );
  if (
    unassigned.length &&
    (allDxCodes.length <= 1 ||
      (rowMessageHasCode(unassigned, code) && unassigned.length === rows.length))
  ) {
    return unassigned;
  }
  return rows.filter((row) => rowMessageHasCode([row], code));
};

const rowMessageHasCode = (
  rows: EncoderIcdValidationResult[],
  code: string
): boolean => {
  const needle = normalizeEncoderCode(code);
  if (!needle) return false;
  for (let i = 0; i < rows.length; i += 1) {
    const haystack = `${rows[i].message} ${rows[i].note} ${rows[i].instruction}`;
    if (normalizeEncoderCode(haystack).indexOf(needle) >= 0) return true;
  }
  return false;
};

const toEditorCode = (result: CodeSearchResult | EncoderScratchCode): EditorCode => {
  const labels = (result.labels ?? []).filter(
    (label) => label !== "Rx" && label !== "HCC"
  );
  if (result.metadata?.Rx?.v08 !== undefined) labels.push(`Rx-${result.metadata.Rx.v08}`);
  if (result.metadata?.HCC?.v24 !== undefined) labels.push(`HCC-${result.metadata.HCC.v24}`);
  if (result.metadata?.HCC?.v28 !== undefined) labels.push(`HCC-${result.metadata.HCC.v28}`);
  return {
    id: "id" in result ? result.id : newScratchId(),
    code: result.code,
    description: result.description,
    type: result.type,
    labels,
    metadata: result.metadata,
  };
};

const clearReviewState = (
  setShowResults: (value: boolean) => void,
  setValidationResults: (value: EncoderIcdValidationResult[]) => void,
  setCptResults: (value: CptEditsResult[]) => void,
  setAnalyzing: (value: boolean) => void
) => {
  setShowResults(false);
  setValidationResults([]);
  setCptResults([]);
  setAnalyzing(false);
};

const CodeEditorTab: React.FC = () => {
  const catalog = useEncoderCatalog();
  const { showToast } = useEncoderUi();
  const years: number[] = catalog.data?.codeYears?.length
    ? catalog.data.codeYears
    : [2026, 2025, 2024, 2023, 2022];
  const [year, setYear] = useState(years[0] ?? 2026);
  const [type, setType] = useState("All");
  const [selected, setSelected] = useState<CodeSearchResult | null>(null);
  const [codes, setCodes] = useState<EditorCode[]>([]);
  const [draggedId, setDraggedId] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [validationResults, setValidationResults] = useState<
    EncoderIcdValidationResult[]
  >([]);
  const [cptResults, setCptResults] = useState<CptEditsResult[]>([]);
  const [resultTab, setResultTab] = useState<"icd" | "cpt">("icd");
  const [selectedIcd, setSelectedIcd] = useState<string | null>(null);
  const [selectedCpt, setSelectedCpt] = useState<string | null>(null);

  const validate = useEncoderCodeValidate();
  const cptEdits = useEncoderCptEdits();
  const icdResultCodes = useMemo(
    () =>
      codes
        .filter((code) => isEncoderIcdResultType(code.type))
        .map((code) => code.code),
    [codes]
  );
  const dxCodes = useMemo(
    () =>
      codes
        .filter((code) => isEncoderDxType(code.type))
        .map((code) => code.code),
    [codes]
  );
  const cptCodes = useMemo(
    () =>
      codes
        .filter((code) => isEncoderCptResultType(code.type))
        .map((code) => code.code),
    [codes]
  );
  const instructionCodes = useMemo(() => {
    const out: string[] = [];
    const seen: Record<string, boolean> = {};
    const add = (value?: string | null) => {
      const text = (value || "").trim();
      if (!text) return;
      const key = normalizeEncoderCode(text);
      if (!key || seen[key]) return;
      seen[key] = true;
      out.push(text);
    };
    for (let i = 0; i < icdResultCodes.length; i += 1) add(icdResultCodes[i]);
    add(selectedIcd);
    return out;
  }, [icdResultCodes, selectedIcd]);
  const instructions = useEncoderInstructions(instructionCodes, {
    enabled: instructionCodes.length > 0,
  });

  const addSelected = () => {
    if (!selected) return;
    setCodes((current) => current.concat([toEditorCode(selected)]));
    setSelected(null);
    clearReviewState(
      setShowResults,
      setValidationResults,
      setCptResults,
      setAnalyzing
    );
  };

  const runReview = async () => {
    if (!codes.length) return;
    setShowResults(true);
    setAnalyzing(true);
    setSelectedIcd(icdResultCodes[0] ?? null);
    setSelectedCpt(cptCodes[0] ?? null);
    setResultTab(icdResultCodes.length ? "icd" : "cpt");
    try {
      const jobs: Array<Promise<unknown>> = [];
      if (dxCodes.length) jobs.push(validate.mutateAsync(dxCodes));
      if (cptCodes.length) {
        jobs.push(
          cptEdits.mutateAsync({
            codes: cptCodes,
            dateofService: todayDdMmYyyy(),
          })
        );
      }
      const instructionJob = icdResultCodes.length ? instructions.refetch() : null;
      const settled = await Promise.allSettled(jobs);
      if (instructionJob) await instructionJob.catch(() => undefined);
      let icdRows: EncoderIcdValidationResult[] = [];
      let cptRows: CptEditsResult[] = [];
      let failed = false;
      for (let i = 0; i < settled.length; i += 1) {
        const outcome = settled[i];
        if (outcome.status === "rejected") {
          failed = true;
          continue;
        }
        const value = outcome.value as {
          results?: EncoderIcdValidationResult[] | CptEditsResult[];
        };
        const rows = value.results ?? [];
        if (dxCodes.length && i === 0) {
          icdRows = rows as EncoderIcdValidationResult[];
        } else {
          cptRows = rows as CptEditsResult[];
        }
      }
      setValidationResults(icdRows);
      setCptResults(cptRows);
      if (failed) {
        showToast(
          "Some encoder checks failed. Showing the results that completed.",
          "error"
        );
      }
    } catch (error) {
      showToast(
        `Encoder failed: ${encoderErrorMessage(error, "Unknown error")}`,
        "error"
      );
    } finally {
      setAnalyzing(false);
    }
  };

  const move = (index: number, delta: number) => {
    const nextIndex = index + delta;
    setCodes((current) => {
      if (nextIndex < 0 || nextIndex >= current.length) return current;
      const next = current.slice();
      const moved = next[index];
      next.splice(index, 1);
      next.splice(nextIndex, 0, moved);
      return next;
    });
    clearReviewState(
      setShowResults,
      setValidationResults,
      setCptResults,
      setAnalyzing
    );
  };

  const removeCode = (id: string) => {
    const next = codes.filter((item) => item.id !== id);
    setCodes(next);
    if (!next.length) {
      clearReviewState(
        setShowResults,
        setValidationResults,
        setCptResults,
        setAnalyzing
      );
    } else {
      setShowResults(false);
      setValidationResults([]);
      setCptResults([]);
    }
  };

  const selectedScratch = codes.find((item) =>
    sameEncoderCode(item.code, selectedIcd ?? "")
  );
  const issuesForCode = (code: string): EncoderIcdValidationResult[] => {
    const fromValidate = resultsForCode(
      validationResults,
      code,
      dxCodes
    ).filter((row) => validationSeverity(row) != null);
    if (fromValidate.length) return fromValidate;
    return alertsFromIcdInstructions(
      code,
      findAggregatedIcdCode(instructions.data, code)
    );
  };
  const selectedInstruction = useMemo(
    () => findAggregatedIcdCode(instructions.data, selectedIcd ?? ""),
    [instructions.data, selectedIcd]
  );
  const selectedIcdIssues = issuesForCode(selectedIcd ?? "");
  const selectedErrors = selectedIcdIssues.filter(
    (row) => validationSeverity(row) === "error"
  );
  const selectedWarnings = selectedIcdIssues.filter(
    (row) => validationSeverity(row) === "warning"
  );
  const selectedInfo = selectedIcdIssues.filter(
    (row) => validationSeverity(row) === "info"
  );
  const selectedCptRows = cptResults.filter((row) =>
    sameEncoderCode(row.column1, selectedCpt ?? "")
  );
  const cptHasEdits = selectedCptRows.some((row) => (row.combo ?? []).length > 0);
  const activeResultTab =
    resultTab === "cpt" && cptCodes.length > 0
      ? "cpt"
      : icdResultCodes.length > 0
        ? "icd"
        : "cpt";

  return (
    <section className="flex h-full min-h-0 w-full flex-col">
      <div className="sticky top-0 z-20 border-b border-gray-200 bg-white p-2 shadow-sm">
        <div className="flex w-full items-center gap-2">
          <div className="flex min-w-0 max-w-[70%] flex-1 items-center gap-2">
            <MergedCodeSearch
              year={year}
              years={years}
              type={type}
              types={["All", "Dx", "PCS", "CPT", "HCPCS"]}
              onYearChange={setYear}
              onTypeChange={setType}
              onSelect={setSelected}
              selected={selected}
            />
            <button
              type="button"
              className="inline-flex h-9 shrink-0 items-center gap-1 rounded-md bg-slate-200 px-3 text-sm font-medium text-slate-800 disabled:opacity-50"
              onClick={addSelected}
              disabled={!selected}
            >
              <IconPlus size={14} /> Add Code
            </button>
            <button
              type="button"
              className="inline-flex h-9 shrink-0 items-center gap-1 rounded-md bg-slate-900 px-3 text-sm text-white"
              onClick={() => {
                setCodes([]);
                setSelected(null);
                clearReviewState(
                  setShowResults,
                  setValidationResults,
                  setCptResults,
                  setAnalyzing
                );
              }}
            >
              <IconRotate size={14} /> Reset
            </button>
          </div>
        </div>
      </div>
      <div className="min-h-0 flex-1 overflow-hidden p-2">
        <div className="flex h-full gap-3">
          <div
            className={cn(
              "flex h-full min-h-0 flex-col overflow-hidden rounded-md border border-slate-200 bg-white",
              codes.length > 0 || showResults ? "w-2/6" : "w-full"
            )}
          >
            <div className="min-h-0 flex-1 overflow-auto">
              {codes.map((code, index) => (
                <div
                  key={code.id}
                  draggable
                  onDragStart={() => setDraggedId(code.id)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={() => {
                    if (!draggedId || draggedId === code.id) return;
                    setCodes((current) => {
                      const next = current.slice();
                      const from = next.findIndex((item) => item.id === draggedId);
                      const to = next.findIndex((item) => item.id === code.id);
                      if (from < 0 || to < 0) return current;
                      const moved = next[from];
                      next.splice(from, 1);
                      next.splice(to, 0, moved);
                      return next;
                    });
                    setDraggedId(null);
                    setShowResults(false);
                    setValidationResults([]);
                    setCptResults([]);
                  }}
                  className="flex min-h-[60px] items-start gap-2 border-b border-gray-200 px-2 py-2"
                >
                  <div className="w-5 shrink-0 pt-0.5 text-sm text-slate-600">
                    {index + 1}.
                  </div>
                  <div className="w-[72px] shrink-0 font-mono text-sm font-semibold">
                    {code.code}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="break-words text-sm text-slate-800">
                      {code.description}
                    </p>
                    <div className="mt-0.5">
                      <CodeLabels
                        result={code}
                        colors={catalog.data?.labelColors}
                      />
                    </div>
                  </div>
                  <div className="flex w-[72px] shrink-0 items-center justify-end gap-1">
                    <button
                      type="button"
                      onClick={() => move(index, -1)}
                      disabled={index === 0}
                      className="text-blue-600 disabled:opacity-30"
                    >
                      <IconArrowUp size={14} />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(index, 1)}
                      disabled={index === codes.length - 1}
                      className="text-blue-600 disabled:opacity-30"
                    >
                      <IconArrowDown size={14} />
                    </button>
                    <button type="button" onClick={() => removeCode(code.id)}>
                      <IconX size={14} className="text-red-500" />
                    </button>
                  </div>
                </div>
              ))}
              {codes.length > 0 ? (
                <div className="flex justify-end border-t border-gray-300 bg-gray-100 p-2">
                  <button
                    type="button"
                    className="h-9 rounded-md bg-slate-900 px-4 text-sm font-medium text-white disabled:opacity-50"
                    onClick={() => void runReview()}
                    disabled={analyzing}
                  >
                    {analyzing ? "Reviewing..." : "Run Review"}
                  </button>
                </div>
              ) : (
                <p className="p-6 text-sm text-slate-500">
                  Search and add ICD or CPT codes, then run review.
                </p>
              )}
            </div>
          </div>

          {(codes.length > 0 || showResults) && (
            <div className="flex h-full min-h-0 w-4/6 flex-col overflow-hidden rounded-md border border-slate-200 bg-white">
              {analyzing ? (
                <div className="flex flex-1 flex-col items-center justify-center gap-2">
                  <img src="/ai-bulwark-logo.svg" alt="" className="h-12 w-12" />
                  <p className="text-3xl font-bold text-[#820ca5]/90">Bulwark Health AI</p>
                  <p className="text-xl font-bold">Analyzing codes...</p>
                </div>
              ) : showResults ? (
                <div className="flex h-full min-h-0 flex-col overflow-hidden">
                  {icdResultCodes.length > 0 || cptCodes.length > 0 ? (
                    <div className="flex h-11 w-full shrink-0 border-b-2 border-gray-300 bg-gray-100">
                      {icdResultCodes.length > 0 ? (
                        <button
                          type="button"
                          onClick={() => setResultTab("icd")}
                          className={cn(
                            "h-11 min-w-0 flex-1 px-6 text-sm font-semibold",
                            activeResultTab === "icd"
                              ? "border border-blue-600 bg-white text-blue-700"
                              : "text-gray-600 hover:bg-white/50 hover:text-blue-700"
                          )}
                        >
                          Dx Instructions
                        </button>
                      ) : null}
                      {cptCodes.length > 0 ? (
                        <button
                          type="button"
                          onClick={() => setResultTab("cpt")}
                          className={cn(
                            "h-11 min-w-0 flex-1 px-6 text-sm font-semibold whitespace-nowrap",
                            activeResultTab === "cpt"
                              ? "border border-blue-600 bg-white text-blue-700"
                              : "text-gray-600 hover:bg-white/50 hover:text-blue-700"
                          )}
                        >
                          Medicare NCCI Procedure to Procedure (PTP) Edits
                        </button>
                      ) : null}
                    </div>
                  ) : null}
                  {activeResultTab === "icd" ? (
                    icdResultCodes.length ? (
                      <div className="flex min-h-0 flex-1 overflow-hidden">
                        <div className="w-[22%] shrink-0 overflow-y-auto border-r bg-slate-50">
                          {icdResultCodes.map((code) => {
                            const rows = issuesForCode(code);
                            const warningCount = rows.filter(
                              (row) => validationSeverity(row) === "warning"
                            ).length;
                            const errorCount = rows.filter(
                              (row) => validationSeverity(row) === "error"
                            ).length;
                            const hasExcludes = rows.some((row) =>
                              row.instruction.toLowerCase().includes("excludes")
                            );
                            return (
                              <button
                                key={code}
                                type="button"
                                className={cn(
                                  "flex w-full items-center justify-between border-b border-slate-200 px-3 py-2 text-left font-mono text-sm",
                                  sameEncoderCode(selectedIcd ?? "", code)
                                    ? "bg-blue-200 font-semibold text-blue-900"
                                    : "bg-white hover:bg-slate-100"
                                )}
                                onClick={() => setSelectedIcd(code)}
                              >
                                <span>{code}</span>
                                <span className="flex items-center gap-1">
                                  {hasExcludes ? (
                                    <span className="flex size-4 items-center justify-center rounded bg-red-500 text-[10px] font-bold text-white">
                                      Ex
                                    </span>
                                  ) : null}
                                  {errorCount > 0 ? (
                                    <span className="flex size-4 items-center justify-center rounded bg-red-500 text-[10px] font-bold text-white">
                                      {errorCount}
                                    </span>
                                  ) : null}
                                  {warningCount > 0 ? (
                                    <span className="flex size-4 items-center justify-center rounded bg-yellow-500 text-[10px] font-bold text-white">
                                      {warningCount}
                                    </span>
                                  ) : null}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                        <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-white">
                          <div className="shrink-0 border-b-2 border-gray-300 px-3 py-2">
                            <h3 className="text-base font-semibold text-blue-700">
                              {selectedIcd}{" "}
                              {selectedScratch?.description
                                ? selectedScratch.description
                                : selectedInstruction?.description || ""}
                            </h3>
                          </div>
                          <div className="flex min-h-0 flex-1 overflow-hidden">
                            <div className="min-h-0 w-1/2 overflow-y-auto border-r border-gray-200 p-3">
                              {selectedIcdIssues.length === 0 ? (
                                <div className="rounded-lg border border-green-200 bg-green-50 p-3">
                                  <p className="flex items-center gap-2 font-medium text-green-800">
                                    <span className="flex size-6 items-center justify-center rounded-full bg-green-500 text-sm font-bold text-white">
                                      <IconCheck size={14} />
                                    </span>
                                    All validation checks passed
                                  </p>
                                  <p className="mt-1 text-xs text-green-600">
                                    This code has no issues or conflicts
                                  </p>
                                </div>
                              ) : (
                                <div className="space-y-4">
                                  {selectedErrors.length > 0 ? (
                                    <div>
                                      <h4 className="mb-3 text-sm font-semibold text-red-700">
                                        Errors ({selectedErrors.length})
                                      </h4>
                                      <div className="space-y-3">
                                        {selectedErrors.map((row, index) => (
                                          <ValidationResultCard
                                            key={`error-${row.instruction}-${index}`}
                                            result={row}
                                            severity="error"
                                          />
                                        ))}
                                      </div>
                                    </div>
                                  ) : null}
                                  {selectedWarnings.length > 0 ? (
                                    <div>
                                      <h4 className="mb-3 text-sm font-semibold text-yellow-700">
                                        Warnings ({selectedWarnings.length})
                                      </h4>
                                      <div className="space-y-3">
                                        {selectedWarnings.map((row, index) => (
                                          <ValidationResultCard
                                            key={`warning-${row.instruction}-${index}`}
                                            result={row}
                                            severity="warning"
                                          />
                                        ))}
                                      </div>
                                    </div>
                                  ) : null}
                                  {selectedInfo.length > 0 ? (
                                    <div>
                                      <h4 className="mb-3 text-sm font-semibold text-blue-700">
                                        Information ({selectedInfo.length})
                                      </h4>
                                      <div className="space-y-3">
                                        {selectedInfo.map((row, index) => (
                                          <ValidationResultCard
                                            key={`info-${row.instruction}-${index}`}
                                            result={row}
                                            severity="info"
                                          />
                                        ))}
                                      </div>
                                    </div>
                                  ) : null}
                                </div>
                              )}
                            </div>
                            <div className="min-h-0 w-1/2 overflow-y-auto p-3">
                              <h4 className="mb-4 text-lg font-semibold text-green-700">
                                Instructions
                              </h4>
                              {instructions.isError ? (
                                <p className="py-4 text-center text-sm text-red-600">
                                  {encoderErrorMessage(
                                    instructions.error,
                                    "Failed to load ICD-10 instructions"
                                  )}
                                </p>
                              ) : instructions.isFetching && !selectedInstruction ? (
                                <Spinner label="Loading instructions…" />
                              ) : (
                                <IcdInstructionCards data={selectedInstruction} />
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <p className="p-6 text-sm text-slate-500">
                        Add an ICD diagnosis code to see Dx Instructions.
                      </p>
                    )
                  ) : (
                    <div className="grid min-h-0 flex-1 grid-cols-10 overflow-hidden">
                      <div className="col-span-2 overflow-auto border-r bg-slate-50 p-2">
                        {cptCodes.map((code) => (
                          <button
                            key={code}
                            type="button"
                            className={cn(
                              "mb-1 w-full rounded px-2 py-2 text-left font-mono text-sm",
                              sameEncoderCode(selectedCpt ?? "", code)
                                ? "bg-blue-200 font-semibold text-blue-900"
                                : "hover:bg-white"
                            )}
                            onClick={() => setSelectedCpt(code)}
                          >
                            {code}
                          </button>
                        ))}
                      </div>
                      <div className="col-span-8 overflow-auto p-4">
                        <h3 className="mb-3 font-semibold text-blue-700">
                          {selectedCpt}{" "}
                          {codes.find((item) =>
                            sameEncoderCode(item.code, selectedCpt ?? "")
                          )?.description ?? ""}
                        </h3>
                        {!cptHasEdits ? (
                          <div className="rounded-md border border-green-200 bg-green-50 p-3">
                            <p className="flex items-center gap-2 font-semibold text-green-800">
                              <IconCheck size={16} /> All validation checks passed
                            </p>
                            <p className="text-sm text-green-700">
                              This code has no NCCI edits or conflicts
                            </p>
                          </div>
                        ) : (
                          <table className="min-w-full text-left text-sm">
                            <thead>
                              <tr className="border-b bg-yellow-100 text-yellow-900">
                                <th className="px-2 py-1">Code</th>
                                <th className="px-2 py-1">Rationale</th>
                                <th className="px-2 py-1">Modifier</th>
                                <th className="px-2 py-1">RVU</th>
                              </tr>
                            </thead>
                            <tbody>
                              {selectedCptRows.flatMap((row) =>
                                (row.combo ?? []).map((combo) => (
                                  <tr
                                    key={`${row.column1}-${combo.code}`}
                                    className="border-b"
                                  >
                                    <td className="px-2 py-1 font-mono">{combo.code}</td>
                                    <td className="px-2 py-1">{combo.rationale}</td>
                                    <td className="px-2 py-1">
                                      {combo.modifierIndicator}
                                    </td>
                                    <td className="px-2 py-1">{combo.RVU}</td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-1 items-center justify-center text-sm text-slate-500">
                  Click Run Review to analyze the added codes.
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default CodeEditorTab;
